import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Alert, Spinner, Badge } from 'react-bootstrap';
import api, { PlatformOverview, PlatformUser } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

type SortKey = 'username' | 'tier' | 'recordCount' | 'createdAt';
type SortDirection = 'asc' | 'desc';

const PlatformAdminDashboard: React.FC = () => {
  const [overview, setOverview] = useState<PlatformOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // null = server order (newest first), which is the default.
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const { userId: currentUserId } = useAuth();

  useEffect(() => {
    loadOverview();
  }, []);

  const loadOverview = async () => {
    try {
      setLoading(true);
      const data = await api.getPlatformOverview();
      setOverview(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load platform overview');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const sortIndicator = (key: SortKey) => {
    if (key !== sortKey) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const sortUsers = (users: PlatformUser[]): PlatformUser[] => {
    if (!sortKey) {
      return users;
    }
    const factor = sortDirection === 'asc' ? 1 : -1;
    return [...users].sort((a, b) => {
      let comparison: number;
      switch (sortKey) {
        // Numeric, not lexicographic — otherwise 10 sorts before 9.
        case 'recordCount':
          comparison = a.recordCount - b.recordCount;
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
        default:
          comparison = String(a[sortKey]).localeCompare(String(b[sortKey]));
      }
      return comparison * factor;
    });
  };

  if (loading) {
    return (
      <Container className="py-4">
        <Card>
          <Card.Body className="text-center py-4">
            <Spinner animation="border" />
          </Card.Body>
        </Card>
      </Container>
    );
  }

  if (error || !overview) {
    return (
      <Container className="py-4">
        <Alert variant="danger">{error || 'Failed to load platform overview'}</Alert>
      </Container>
    );
  }

  const { totals } = overview;
  const users = sortUsers(overview.users);

  return (
    <Container className="py-4">
      <Card style={{ borderTop: '10px solid #dc3545' }}>
        <Card.Header className="bg-danger text-white">
          <h5 className="mb-0">Platform Overview (Admin Only)</h5>
        </Card.Header>
        <Card.Body>
          <div className="d-flex flex-wrap gap-4 mb-4">
            <div>
              <div className="text-muted small">Users</div>
              <div className="fs-4 fw-bold">{totals.userCount}</div>
            </div>
            <div>
              <div className="text-muted small">Pro</div>
              <div className="fs-4 fw-bold">{totals.proCount}</div>
            </div>
            <div>
              <div className="text-muted small">Free</div>
              <div className="fs-4 fw-bold">{totals.freeCount}</div>
            </div>
            <div>
              <div className="text-muted small">Records</div>
              <div className="fs-4 fw-bold">{totals.recordCount}</div>
            </div>
          </div>

          <Table responsive striped bordered hover size="sm">
            <thead>
              <tr>
                <th role="button" onClick={() => handleSort('username')}>
                  Username{sortIndicator('username')}
                </th>
                <th role="button" onClick={() => handleSort('tier')}>
                  Tier{sortIndicator('tier')}
                </th>
                <th role="button" onClick={() => handleSort('recordCount')}>
                  Records{sortIndicator('recordCount')}
                </th>
                <th role="button" onClick={() => handleSort('createdAt')}>
                  Created{sortIndicator('createdAt')}
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>
                    {user.username}
                    {user._id === currentUserId && <Badge bg="secondary" className="ms-2">You</Badge>}
                  </td>
                  <td>
                    <Badge bg={user.tier === 'pro' ? 'success' : 'secondary'}>
                      {user.tier}
                    </Badge>
                  </td>
                  <td>{user.recordCount}</td>
                  <td>{new Date(user.createdAt).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default PlatformAdminDashboard;
